import React, { useState, useRef, useEffect } from 'react';

export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  size = 'md',
  style,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownRect, setDropdownRect] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => String(o.value) === String(value));
  const filtered = options.filter(o =>
    o.label?.toLowerCase().includes(query.toLowerCase())
  );

  const computeRect = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Flip to open upward when there isn't enough room below but there is above —
      // otherwise the list silently runs off the bottom of the viewport.
      const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
      setDropdownRect({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
        openUp,
        maxListHeight: Math.max(120, Math.min(220, (openUp ? spaceAbove : spaceBelow) - 56)),
      });
    }
  };

  useEffect(() => {
    if (!open) { setQuery(''); setDropdownRect(null); return; }
    computeRect();
    window.addEventListener('scroll', computeRect, true);
    window.addEventListener('resize', computeRect);
    return () => {
      window.removeEventListener('scroll', computeRect, true);
      window.removeEventListener('resize', computeRect);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
  };

  const isSm = size === 'sm';
  const triggerHeight = isSm ? 31 : 38;

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: triggerHeight,
          padding: isSm ? '0 0.5rem' : '0 0.75rem',
          border: '1px solid var(--bs-border-color, #dee2e6)',
          borderRadius: isSm ? 'var(--bs-border-radius-sm, 0.25rem)' : 'var(--bs-border-radius, 0.375rem)',
          backgroundColor: disabled ? 'var(--bs-secondary-bg, #e9ecef)' : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          boxSizing: 'border-box',
        }}
        onClick={() => { if (!disabled) setOpen(v => !v); }}
      >
        <span style={{ color: selected ? 'inherit' : '#6c757d', fontSize: isSm ? 13 : 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ color: '#6c757d', fontSize: 10, marginLeft: 4 }}>▼</span>
      </div>

      {open && dropdownRect && (
        <div style={{
          position: 'fixed',
          ...(dropdownRect.openUp
            ? { bottom: window.innerHeight - dropdownRect.top + 2 }
            : { top: dropdownRect.bottom + 2 }),
          left: dropdownRect.left,
          width: dropdownRect.width,
          zIndex: 9999,
          background: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
            <input
              ref={inputRef}
              type="text"
              className="form-control form-control-sm"
              placeholder="Type to search…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div style={{ maxHeight: dropdownRect.maxListHeight, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '8px 12px', color: '#6c757d', fontSize: 13 }}>No results</div>
            ) : (
              filtered.map(opt => (
                <div
                  key={opt.value}
                  onMouseDown={() => handleSelect(opt)}
                  style={{
                    padding: '7px 12px',
                    fontSize: isSm ? 13 : 14,
                    cursor: 'pointer',
                    background: String(opt.value) === String(value) ? '#e8f0fe' : 'transparent',
                    fontWeight: String(opt.value) === String(value) ? 600 : 400,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={e => e.currentTarget.style.background = String(opt.value) === String(value) ? '#e8f0fe' : 'transparent'}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
